import { createContext, useContext, useEffect, useMemo, useState } from "react";
import en from "./dictionaries/en";
import es from "./dictionaries/es";
import { track } from "../lib/analytics";

const dictionaries = { en, es };
const STORAGE_KEY = "gfirem.lang";

// Dev-only: walk both dictionaries and log any keys that exist in one but not the other.
// This catches the "island-flavors intro missing in ES" class of bugs at boot time.
function collectPaths(obj, prefix = "", acc = new Set()) {
    if (obj == null || typeof obj !== "object") {
        acc.add(prefix);
        return acc;
    }
    if (Array.isArray(obj)) {
        obj.forEach((item, i) => collectPaths(item, `${prefix}[${i}]`, acc));
        return acc;
    }
    for (const k of Object.keys(obj)) {
        const nextKey = prefix ? `${prefix}.${k}` : k;
        collectPaths(obj[k], nextKey, acc);
    }
    return acc;
}

function assertDictionariesInSync() {
    if (process.env.NODE_ENV === "production") return;
    const enPaths = collectPaths(en);
    const esPaths = collectPaths(es);
    const missingInEs = [...enPaths].filter((p) => !esPaths.has(p));
    const missingInEn = [...esPaths].filter((p) => !enPaths.has(p));
    if (missingInEs.length) {
        console.warn(
            `[i18n] ${missingInEs.length} key(s) present in EN but missing in ES:`,
            missingInEs
        );
    }
    if (missingInEn.length) {
        console.warn(
            `[i18n] ${missingInEn.length} key(s) present in ES but missing in EN:`,
            missingInEn
        );
    }
}
assertDictionariesInSync();

const LanguageContext = createContext({
    lang: "en",
    setLang: () => {},
    t: () => "",
});

function readUrlLang() {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const raw = (params.get("lang") || "").toLowerCase();
    return raw === "en" || raw === "es" ? raw : null;
}

function detectLang() {
    if (typeof window === "undefined") return "en";
    const fromUrl = readUrlLang();
    if (fromUrl) return fromUrl;
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "en" || saved === "es") return saved;
    const browser = (navigator.language || "en").toLowerCase();
    return browser.startsWith("es") ? "es" : "en";
}

function syncUrlLang(lang) {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.set("lang", lang);
    window.history.replaceState(null, "", url.toString());
}

function syncOgLocale(lang) {
    if (typeof document === "undefined") return;
    const tag = document.querySelector('meta[property="og:locale"]');
    if (tag) tag.setAttribute("content", lang === "es" ? "es_LA" : "en_US");
}

function resolvePath(dict, path) {
    const parts = path.split(".");
    let cursor = dict;
    for (const p of parts) {
        if (cursor == null) return undefined;
        cursor = cursor[p];
    }
    return cursor;
}

export function LanguageProvider({ children }) {
    const [lang, setLangState] = useState(() => detectLang());

    useEffect(() => {
        if (typeof document !== "undefined") {
            document.documentElement.lang = lang === "es" ? "es" : "en";
        }
        syncOgLocale(lang);
        if (typeof window !== "undefined") {
            const params = new URLSearchParams(window.location.search);
            if (params.get("lang") !== lang) {
                syncUrlLang(lang);
            }
        }
    }, [lang]);

    const setLang = (next) => {
        if (next !== "en" && next !== "es") return;
        if (next === lang) return;
        window.localStorage.setItem(STORAGE_KEY, next);
        syncUrlLang(next);
        // Fires BEFORE setLangState so the previous lang is still what
        // getLang() would resolve to in the analytics payload — we override
        // via explicit props for accuracy.
        track("lang_toggled", { from: lang, to: next });
        setLangState(next);
    };

    const value = useMemo(() => {
        const dict = dictionaries[lang] || dictionaries.en;
        const t = (path) => {
            const value = resolvePath(dict, path);
            if (value === undefined) {
                const fallback = resolvePath(dictionaries.en, path);
                return fallback === undefined ? path : fallback;
            }
            return value;
        };
        return { lang, setLang, t };
    }, [lang]);

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useT() {
    return useContext(LanguageContext);
}
